// EP-161 §2.3, S6 (T161-W02a): "รับเงินสด" at the gate. The cash is saved
// only with a camera photo of it (the server requires cash_photo_key for a
// cash settlement and stamps the photo with its own clock). The amount is the
// server's charge, read only, when the session has a payment; an overstayed
// car has none, so its amount is typed.
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Camera } from 'lucide-react'
import { Alert, Button, Input, Modal } from '@/components/ui'
import { useCashPhoto } from '@/hooks/useParkingSessions'
import { useSettleParkingSession } from '@/hooks/useLprEvents'
import { formatBaht } from '@/lib/money'
import { cashPhotoErrorShown, isPhotoRefusal, settleErrorCode } from '@/lib/parkingPhotoUpload'
import { useNotificationStore } from '@/stores'
import type { ParkingSession } from '@/types/parking'

export type CashPhotoState = ReturnType<typeof useCashPhoto>

/**
 * The photo step: a button opening the back camera in the app (on a desktop
 * browser the same input is a file picker), the thumbnail with "ถ่ายใหม่".
 * The photo uploads the moment it is taken.
 */
export function CashPhotoCapture({ state, disabled }: { state: CashPhotoState; disabled?: boolean }) {
  const { t } = useTranslation('parking')
  const input = useRef<HTMLInputElement>(null)
  const { photo, uploading, error, select } = state
  const open = () => input.current?.click()
  const shownError = cashPhotoErrorShown(error)

  return (
    <div className="space-y-2">
      <input
        ref={input}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        data-testid="cash-photo-input"
        onChange={(e) => {
          const file = e.target.files?.[0]
          e.target.value = ''
          if (file) void select(file)
        }}
      />
      {shownError && (
        <Alert variant="error" data-testid="cash-photo-error">
          {t(`sessions.gate.errors.${shownError}`)}
        </Alert>
      )}
      {photo ? (
        <div className="space-y-2">
          {photo.previewUrl && (
            <img
              src={photo.previewUrl}
              alt={t('sessions.cash_photo')}
              className="w-full max-h-64 object-contain rounded-lg bg-base-200"
              data-testid="cash-photo-preview"
            />
          )}
          <Button size="sm" variant="outline" onClick={open} disabled={disabled || uploading} loading={uploading}>
            {t('sessions.gate.retake')}
          </Button>
        </div>
      ) : (
        <div className="space-y-1">
          <Button
            variant="outline"
            className="w-full"
            onClick={open}
            disabled={disabled || uploading}
            loading={uploading}
            leftIcon={<Camera className="h-4 w-4" aria-hidden="true" />}
          >
            {uploading ? t('sessions.gate.uploading_photo') : t('sessions.gate.take_cash_photo')}
          </Button>
          <p className="text-xs text-base-content/60">{t('sessions.gate.cash_photo_hint')}</p>
        </div>
      )}
    </div>
  )
}

export interface CashSettleModalProps {
  session: ParkingSession
  onClose: () => void
}

export function CashSettleModal({ session, onClose }: CashSettleModalProps) {
  const { t } = useTranslation('parking')
  const addToast = useNotificationStore((s) => s.addToast)
  const settle = useSettleParkingSession()
  const photo = useCashPhoto(session.id)
  const [typed, setTyped] = useState('')
  const [error, setError] = useState<string | null>(null)

  // A payment carries the server's charge; only a session without one (the
  // overstay case) takes a typed amount.
  const charge = session.payment ? (session.payment.amount ?? session.charge_amount) : null
  const needsAmount = !session.payment
  const typedAmount = Number(typed)
  const amountValid = !needsAmount || (typed.trim() !== '' && Number.isFinite(typedAmount) && typedAmount > 0)
  const canSave = !!photo.photo && !photo.uploading && amountValid

  async function save() {
    if (!photo.photo || !amountValid) return
    setError(null)
    try {
      await settle.mutateAsync({
        parking_session_id: session.id,
        payment_method: 'cash',
        cash_photo_key: photo.photo.key,
        ...(needsAmount ? { amount: typedAmount } : {}),
      })
      addToast({ type: 'success', message: t('sessions.gate.cash_saved') })
      onClose()
    } catch (e) {
      const code = settleErrorCode(e)
      if (isPhotoRefusal(code)) photo.reset(code)
      else setError(code)
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      size="sm"
      title={t('sessions.gate.cash_title', { plate: session.license_plate })}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={settle.isPending}>
            {t('sessions.gate.cancel')}
          </Button>
          <Button onClick={save} loading={settle.isPending} disabled={!canSave} data-testid="cash-settle-save">
            {t('sessions.gate.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && (
          <Alert variant="error" data-testid="cash-settle-error">
            {t(`sessions.gate.errors.${error}`)}
          </Alert>
        )}
        {needsAmount ? (
          <Input
            type="number"
            inputMode="decimal"
            step="0.01"
            min="0.01"
            label={t('sessions.gate.cash_amount_label')}
            leftIcon={<span className="text-sm font-medium">฿</span>}
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            data-testid="cash-settle-amount"
          />
        ) : (
          <p className="font-medium" data-testid="cash-settle-charge">
            {t('sessions.gate.cash_amount_from_charge', { amount: charge == null ? '-' : formatBaht(Number(charge)) })}
          </p>
        )}
        <CashPhotoCapture state={photo} disabled={settle.isPending} />
      </div>
    </Modal>
  )
}
